/* descriptive-statistics.ts */

/* Utils */
import { replaceBooleanStrings } from "src/utils/helper/data-transformation";

/* Constants */
import { ChartType } from "src/shared/constants/charts/Chart";
import { Insights } from "src/shared/constants/common/Common";

/* Interfaces */
import { BaseChartProps } from "src/shared/interfaces/charts/BaseChart";
import { DescriptiveStatisticsProps } from "src/shared/interfaces/DescriptiveStatistics";

/**
 * Get the types for layout.
 *
 * @param {DescriptiveStatisticProps} data - The data to get types from.
 * @returns {ChartType[]}
 */
const getTypes = (
  data: DescriptiveStatisticsProps
): ChartType[] => {
  const types = (data.data ?? []).map(
    (d: { type: string }) => d.type
  );

  return types;
};

const roundStats = (stats: Record<string, any>) => {
  const rounded = Object.fromEntries(
    Object.entries(stats).map(([key, value]) => {
      // Some stats (e.g. std for a single-patient cohort) are legitimately
      // null/undefined - keep them as-is rather than coercing to NaN, which
      // would otherwise render as the literal text "NaN".
      if (value === null || value === undefined) {
        return [key, value];
      }
      return [key, Number(value.toFixed(1))];
    })
  );

  return rounded;
};

const transformChartByType = (chart: any) => {
  switch (chart.type) {
    case ChartType.BAR:
      const transformedData = chart.x.map(
        (cat: string, index: number) => ({
          category: cat,
          value: chart.y[index] ?? 0,
        })
      );
      return { ...chart, transformedData };
    case ChartType.BOX:
      if (chart.stats) {
        chart.stats = roundStats(chart.stats);
      }
      return chart;
    default:
      return chart;
  }
};

/**
 * Transform descriptive statistics data.
 *
 * @param {any} data - The data to transform.
 * @returns {BaseChartProps<DescriptiveStatisticsProps>}
 */
export const transformDescriptiveStatisticsData = (
  data: any
): BaseChartProps<
  DescriptiveStatisticsProps,
  ChartType[]
> => {
  const types = getTypes(data);

  const transformedData = (data.data ?? []).reduce(
    (acc: any, curr: any) => {
      curr.name = curr.name ?? Insights.FULL_SAMPLE;

      if (!acc[curr.type]) acc[curr.type] = [];
      const transformed = transformChartByType(curr);
      acc[curr.type].push(transformed);

      return acc;
    },
    {}
  );

  return {
    total: data.total,
    totalCohortSize: data.totalCohortSize,
    lookedForNoValueTotal: data.lookedForNoValueTotal,
    types: types,
    data: replaceBooleanStrings(transformedData),
  };
};
